// Sprint 11b — Multi-server federation store (per ADR D-007).
//
// Manages the registry of known VPS backends and the currently active one.
// Servers are persisted to localStorage so the list survives page reloads.
// The active server determines the baseUrl for all API + Socket.io calls.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ServerEntry {
  id: string;
  name: string;
  baseUrl: string;
  /** Cached health status from the last probe. */
  status?: 'online' | 'offline' | 'unknown';
  /** Project count from /api/health. */
  projectsCount?: number;
  /** Running agent count from /api/health. */
  runningAgents?: number;
}

export interface ServersState {
  servers: ServerEntry[];
  activeServerId: string | null;

  addServer: (server: Omit<ServerEntry, 'status'>) => void;
  removeServer: (id: string) => void;
  updateServer: (id: string, patch: Partial<Omit<ServerEntry, 'id'>>) => void;
  setActiveServer: (id: string) => void;
  getActiveServer: () => ServerEntry | null;
  probeServer: (id: string) => Promise<void>;
  probeAll: () => Promise<void>;
}

export const useServersStore = create<ServersState>()(
  persist(
    (set, get) => ({
      servers: [],
      activeServerId: null,

      addServer: (server) => {
        set((s) => {
          const exists = s.servers.some((sv) => sv.id === server.id);
          if (exists) {
            return {
              servers: s.servers.map((sv) =>
                sv.id === server.id ? { ...sv, ...server } : sv,
              ),
            };
          }
          const entry: ServerEntry = { ...server, status: 'unknown' };
          const isFirst = s.servers.length === 0;
          return {
            servers: [...s.servers, entry],
            activeServerId: isFirst ? server.id : s.activeServerId,
          };
        });
      },

      removeServer: (id) => {
        set((s) => {
          const next = s.servers.filter((sv) => sv.id !== id);
          const activeGone = s.activeServerId === id;
          return {
            servers: next,
            activeServerId: activeGone ? (next[0]?.id ?? null) : s.activeServerId,
          };
        });
      },

      updateServer: (id, patch) => {
        set((s) => ({
          servers: s.servers.map((sv) =>
            sv.id === id ? { ...sv, ...patch } : sv,
          ),
        }));
      },

      setActiveServer: (id) => {
        set({ activeServerId: id });
      },

      getActiveServer: () => {
        const { servers, activeServerId } = get();
        return servers.find((s) => s.id === activeServerId) ?? servers[0] ?? null;
      },

      probeServer: async (id) => {
        const server = get().servers.find((s) => s.id === id);
        if (!server) return;
        try {
          const res = await fetch(`${server.baseUrl}/api/health`, {
            credentials: 'include',
            signal: AbortSignal.timeout(5000),
          });
          if (!res.ok) {
            get().updateServer(id, { status: 'offline' });
            return;
          }
          const body = (await res.json()) as {
            serverName?: string;
            projectsCount?: number;
            runningAgents?: number;
          };
          get().updateServer(id, {
            status: 'online',
            name: body.serverName ?? server.name,
            projectsCount: body.projectsCount,
            runningAgents: body.runningAgents,
          });
        } catch {
          get().updateServer(id, { status: 'offline' });
        }
      },

      probeAll: async () => {
        const { servers } = get();
        await Promise.allSettled(servers.map((s) => get().probeServer(s.id)));
      },
    }),
    {
      name: 'asoasis-servers',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        servers: state.servers.map(({ id, name, baseUrl }) => ({ id, name, baseUrl })),
        activeServerId: state.activeServerId,
      }),
    },
  ),
);

/** Resolve the base URL for API calls to the active server.
 *  Returns '' when running same-origin (single-server mode). */
export function getActiveBaseUrl(): string {
  const active = useServersStore.getState().getActiveServer();
  if (!active) return '';
  // If the active server baseUrl matches the current origin, use '' for
  // same-origin relative paths (avoids CORS in single-server setups).
  if (typeof window !== 'undefined' && active.baseUrl === window.location.origin) {
    return '';
  }
  return active.baseUrl;
}
